import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import {
  TrendingUp,
  Phone,
  DollarSign,
  Users,
  Target,
  Calendar,
  FileText,
  Play,
  Pause,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  Eye,
  Download,
  Mic,
  Video,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Send,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Métricas de ventas
const salesMetrics = {
  leadsMonth: 45,
  callsMade: 32,
  salesClosed: 6,
  closeRate: 18.75,
  revenueGenerated: 48500,
};

const weeklyData = [
  { week: 'Sem 1', leads: 10, calls: 8, sales: 1 },
  { week: 'Sem 2', leads: 12, calls: 10, sales: 2 },
  { week: 'Sem 3', leads: 11, calls: 7, sales: 1 },
  { week: 'Sem 4', leads: 12, calls: 7, sales: 2 },
];

// Registro de llamadas
const callsRegistry = [
  { 
    id: '1', 
    prospecto: 'Tech Solutions Inc.', 
    fecha: '2026-01-23', 
    duracion: '45 min',
    resultado: 'positivo',
    resumen: 'Muy interesados en el servicio completo. Pidieron propuesta para marketing + web. Presupuesto aproximado 8-10k/mes.',
    proximoPaso: 'Enviar propuesta',
    grabacion: true,
  },
  { 
    id: '2', 
    prospecto: 'StartupX', 
    fecha: '2026-01-22', 
    duracion: '30 min',
    resultado: 'seguimiento',
    resumen: 'Interés en gestión de redes sociales. Presupuesto ajustado, están evaluando opciones.',
    proximoPaso: 'Seguimiento en 1 semana',
    grabacion: true,
  },
  { 
    id: '3', 
    prospecto: 'E-commerce Pro', 
    fecha: '2026-01-20', 
    duracion: '25 min',
    resultado: 'negativo',
    resumen: 'No es buen fit en este momento. Ya tienen equipo interno de marketing.',
    proximoPaso: 'Archivar',
    grabacion: false,
  },
  { 
    id: '4', 
    prospecto: 'Consultoría ABC', 
    fecha: '2026-01-19', 
    duracion: '40 min',
    resultado: 'positivo',
    resumen: 'Necesitan ayuda con captación de leads B2B. Muy alineados con nuestro nicho.',
    proximoPaso: 'Enviar propuesta',
    grabacion: true,
  },
];

// Propuestas
const proposals = [
  { 
    id: '1', 
    cliente: 'Tech Solutions Inc.', 
    servicio: 'Marketing Digital + Web',
    valor: 9500,
    fecha: '2026-01-24',
    estado: 'enviada',
    validez: '2026-02-07',
  },
  { 
    id: '2', 
    cliente: 'Consultoría ABC', 
    servicio: 'Generación de Leads B2B',
    valor: 4500,
    fecha: '2026-01-23',
    estado: 'enviada',
    validez: '2026-02-06',
  },
  { 
    id: '3', 
    cliente: 'Fashion Brand', 
    servicio: 'Gestión Redes Sociales',
    valor: 2500,
    fecha: '2026-01-15',
    estado: 'aceptada',
    validez: '2026-01-29',
  },
  { 
    id: '4', 
    cliente: 'Startup Y', 
    servicio: 'Consultoría Estratégica',
    valor: 3000,
    fecha: '2026-01-10',
    estado: 'rechazada',
    validez: '2026-01-24',
  },
];

const resultadoConfig = {
  positivo: { label: 'Positivo', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  seguimiento: { label: 'Seguimiento', color: 'bg-amber-100 text-amber-700', icon: Clock },
  negativo: { label: 'Negativo', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const estadoProposal = {
  borrador: { label: 'Borrador', color: 'bg-gray-100 text-gray-700' },
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  en_revision: { label: 'En Revisión', color: 'bg-amber-100 text-amber-700' },
  aceptada: { label: 'Aceptada', color: 'bg-emerald-100 text-emerald-700' },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
};

export function SalesPage() {
  const [activeTab, setActiveTab] = useState('metricas');
  const [isAddingCall, setIsAddingCall] = useState(false);
  const [isAddingProposal, setIsAddingProposal] = useState(false);
  const [calls, setCalls] = useState(callsRegistry);
  const [proposalsList, setProposalsList] = useState(proposals);

  const [newCall, setNewCall] = useState({
    prospecto: '',
    fecha: '',
    duracion: '',
    resultado: 'seguimiento',
    resumen: '',
    proximoPaso: '',
  });

  const [newProposal, setNewProposal] = useState({
    cliente: '',
    servicio: '',
    valor: '',
    fecha: '',
    validez: '',
    estado: 'borrador',
  });

  const handleAddCall = () => {
    if (!newCall.prospecto || !newCall.fecha) {
      return;
    }
    const call = { ...newCall, id: Date.now().toString(), grabacion: false };
    setCalls([call, ...calls]);
    setNewCall({ prospecto: '', fecha: '', duracion: '', resultado: 'seguimiento', resumen: '', proximoPaso: '' });
    setIsAddingCall(false);
  };

  const handleAddProposal = () => {
    if (!newProposal.cliente || !newProposal.valor) {
      return;
    }
    const proposal = { ...newProposal, id: Date.now().toString(), valor: parseFloat(newProposal.valor) };
    setProposalsList([proposal, ...proposalsList]);
    setNewProposal({ cliente: '', servicio: '', valor: '', fecha: '', validez: '', estado: 'borrador' });
    setIsAddingProposal(false);
  };

  // Stats calculadas
  const proposalsEnviadas = proposalsList.filter(p => p.estado === 'enviada').length;
  const proposalsAceptadas = proposalsList.filter(p => p.estado === 'aceptada').length;
  const valorEnPipeline = proposalsList.filter(p => p.estado === 'enviada').reduce((sum, p) => sum + p.valor, 0);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="sales-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ventas</h1>
          <p className="text-muted-foreground mt-1">Métricas, llamadas y propuestas</p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          <Calendar className="w-3 h-3 mr-1" />
          Enero 2026
        </Badge>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="metricas">
            <BarChart3 className="w-4 h-4 mr-2" />
            Métricas
          </TabsTrigger>
          <TabsTrigger value="llamadas">
            <Phone className="w-4 h-4 mr-2" />
            Llamadas
          </TabsTrigger>
          <TabsTrigger value="propuestas">
            <FileText className="w-4 h-4 mr-2" />
            Propuestas
          </TabsTrigger>
        </TabsList>

        {/* Métricas Tab */}
        <TabsContent value="metricas" className="space-y-6">
          {/* KPIs */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="card-hover kpi-blue">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                <p className="text-xs text-muted-foreground">Leads del Mes</p>
                <p className="text-2xl font-bold">{salesMetrics.leadsMonth}</p>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-amber">
              <CardContent className="p-4 text-center">
                <Phone className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                <p className="text-xs text-muted-foreground">Llamadas</p>
                <p className="text-2xl font-bold">{salesMetrics.callsMade}</p>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-emerald">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
                <p className="text-xs text-muted-foreground">Ventas Cerradas</p>
                <p className="text-2xl font-bold">{salesMetrics.salesClosed}</p>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-coral">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-xs text-muted-foreground">% Cierre</p>
                <p className="text-2xl font-bold">{salesMetrics.closeRate}%</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-gradient-to-br from-emerald-50 to-blue-50">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Facturación</p>
                <p className="text-xl font-bold text-primary">€{salesMetrics.revenueGenerated.toLocaleString()}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Chart */}
          <motion.div variants={itemVariants}>
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Evolución Semanal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={weeklyData}>
                      <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="week" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Area type="monotone" dataKey="leads" stroke="#3b82f6" fillOpacity={1} fill="url(#colorLeads)" strokeWidth={2} />
                      <Area type="monotone" dataKey="calls" stroke="#f59e0b" fillOpacity={1} fill="url(#colorCalls)" strokeWidth={2} />
                      <Area type="monotone" dataKey="sales" stroke="#10b981" fillOpacity={1} fill="url(#colorSales)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm">Leads</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-sm">Llamadas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm">Ventas</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Resumen Pipeline */}
          <motion.div variants={itemVariants}>
            <Card className="card-hover bg-gradient-to-r from-blue-50 to-emerald-50">
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Propuestas Enviadas</p>
                    <p className="text-3xl font-bold text-blue-700">{proposalsEnviadas}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Valor en Pipeline</p>
                    <p className="text-3xl font-bold text-amber-600">€{valorEnPipeline.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Propuestas Aceptadas</p>
                    <p className="text-3xl font-bold text-emerald-600">{proposalsAceptadas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Llamadas Tab */}
        <TabsContent value="llamadas" className="space-y-6">
          <motion.div variants={itemVariants} className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Registro de Llamadas</h2>
              <p className="text-muted-foreground">Grabaciones y resúmenes de llamadas de ventas</p>
            </div>
            <Dialog open={isAddingCall} onOpenChange={setIsAddingCall}>
              <DialogTrigger asChild>
                <Button className="gradient-primary text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Llamada
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Registrar Llamada</DialogTitle>
                  <DialogDescription>Añade los detalles de la llamada de ventas</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prospecto *</Label>
                      <Input value={newCall.prospecto} onChange={(e) => setNewCall({...newCall, prospecto: e.target.value})} placeholder="Nombre del prospecto" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha *</Label>
                      <Input type="date" value={newCall.fecha} onChange={(e) => setNewCall({...newCall, fecha: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duración</Label>
                      <Input value={newCall.duracion} onChange={(e) => setNewCall({...newCall, duracion: e.target.value})} placeholder="30 min" />
                    </div>
                    <div className="space-y-2">
                      <Label>Resultado</Label>
                      <Select value={newCall.resultado} onValueChange={(v) => setNewCall({...newCall, resultado: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="positivo">Positivo</SelectItem>
                          <SelectItem value="seguimiento">Seguimiento</SelectItem>
                          <SelectItem value="negativo">Negativo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Resumen</Label>
                    <Textarea value={newCall.resumen} onChange={(e) => setNewCall({...newCall, resumen: e.target.value})} placeholder="Puntos clave de la conversación..." rows={3} />
                  </div>
                  <div className="space-y-2">
                    <Label>Próximo Paso</Label>
                    <Input value={newCall.proximoPaso} onChange={(e) => setNewCall({...newCall, proximoPaso: e.target.value})} placeholder="Enviar propuesta, seguimiento, etc." />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setIsAddingCall(false)}>Cancelar</Button>
                    <Button onClick={handleAddCall}>Guardar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-4">
            {calls.map((call) => {
              const config = resultadoConfig[call.resultado];
              const IconResult = config.icon;
              return (
                <Card key={call.id} className="card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{call.prospecto}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(call.fecha).toLocaleDateString('es-ES')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {call.duracion}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={config.color}>
                          <IconResult className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                        {call.grabacion && (
                          <Button variant="outline" size="sm">
                            <Play className="w-4 h-4 mr-1" />
                            Grabación
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {call.resumen && (
                      <div className="p-3 rounded-lg bg-muted/30 mb-3">
                        <p className="text-sm font-medium mb-1">Resumen:</p>
                        <p className="text-sm text-muted-foreground">{call.resumen}</p>
                      </div>
                    )}
                    
                    {call.proximoPaso && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <ArrowUpRight className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">Próximo paso:</span>
                        <span className="text-sm text-blue-600">{call.proximoPaso}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </motion.div>
        </TabsContent>

        {/* Propuestas Tab */}
        <TabsContent value="propuestas" className="space-y-6">
          <motion.div variants={itemVariants} className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Registro de Propuestas</h2>
              <p className="text-muted-foreground">Propuestas enviadas y su estado</p>
            </div>
            <Dialog open={isAddingProposal} onOpenChange={setIsAddingProposal}>
              <DialogTrigger asChild>
                <Button className="gradient-primary text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Propuesta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Nueva Propuesta</DialogTitle>
                  <DialogDescription>Registra una nueva propuesta comercial</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Input value={newProposal.cliente} onChange={(e) => setNewProposal({...newProposal, cliente: e.target.value})} placeholder="Nombre del cliente" />
                  </div>
                  <div className="space-y-2">
                    <Label>Servicio</Label>
                    <Input value={newProposal.servicio} onChange={(e) => setNewProposal({...newProposal, servicio: e.target.value})} placeholder="Tipo de servicio" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Valor (€) *</Label>
                      <Input type="number" value={newProposal.valor} onChange={(e) => setNewProposal({...newProposal, valor: e.target.value})} placeholder="5000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Select value={newProposal.estado} onValueChange={(v) => setNewProposal({...newProposal, estado: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(estadoProposal).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha Envío</Label>
                      <Input type="date" value={newProposal.fecha} onChange={(e) => setNewProposal({...newProposal, fecha: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Válida Hasta</Label>
                      <Input type="date" value={newProposal.validez} onChange={(e) => setNewProposal({...newProposal, validez: e.target.value})} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setIsAddingProposal(false)}>Cancelar</Button>
                    <Button onClick={handleAddProposal}>Guardar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>

          {/* Stats de propuestas */}
          <motion.div variants={itemVariants} className="grid grid-cols-4 gap-4">
            <Card className="bg-gray-50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Borrador</p>
                <p className="text-2xl font-bold">{proposalsList.filter(p => p.estado === 'borrador').length}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Enviadas</p>
                <p className="text-2xl font-bold text-blue-700">{proposalsEnviadas}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Aceptadas</p>
                <p className="text-2xl font-bold text-emerald-700">{proposalsAceptadas}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Rechazadas</p>
                <p className="text-2xl font-bold text-red-700">{proposalsList.filter(p => p.estado === 'rechazada').length}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Lista de propuestas */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Cliente</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Servicio</th>
                      <th className="text-right py-3 px-4 text-sm font-medium">Valor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Validez</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposalsList.map((proposal) => (
                      <tr key={proposal.id} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4 font-medium">{proposal.cliente}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{proposal.servicio}</td>
                        <td className="py-3 px-4 text-right font-semibold">€{proposal.valor.toLocaleString()}</td>
                        <td className="py-3 px-4 text-sm">{proposal.fecha ? new Date(proposal.fecha).toLocaleDateString('es-ES') : '-'}</td>
                        <td className="py-3 px-4 text-sm">{proposal.validez ? new Date(proposal.validez).toLocaleDateString('es-ES') : '-'}</td>
                        <td className="py-3 px-4">
                          <Badge className={estadoProposal[proposal.estado]?.color}>
                            {estadoProposal[proposal.estado]?.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
